import React, { useState, useEffect, useRef, useCallback } from 'react';
import VoiceRecorder from './VoiceRecorder';

interface ChatInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSend: () => void;
  disabled?: boolean;
  loading?: boolean;
  interviewId: string | null;
  onVoiceMessage: (text: string) => void;
  onVoiceResponse: (audioData: string, text: string) => void;
  isPlaying?: boolean;
  onPlaybackStart?: () => void;
  onPlaybackEnd?: () => void;
  onLiveTranscriptionUpdate: (text: string) => void;
  voiceEnabled?: boolean;
  isVoiceConnected?: boolean;
  isVoiceProcessing?: boolean;
  onVoiceToggle?: () => void;
  onVoiceSettingsToggle?: () => void;
  showVoiceSettings?: boolean;
  onWebSocketAudioData?: (audioBlob: Blob, metadata?: any) => void;
  showTextInput?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  loading = false,
  interviewId,
  onVoiceMessage,
  onVoiceResponse,
  isPlaying = false,
  onPlaybackStart,
  onPlaybackEnd,
  onLiveTranscriptionUpdate,
  voiceEnabled = false,
  isVoiceConnected = false,
  isVoiceProcessing = false,
  onVoiceToggle,
  onVoiceSettingsToggle,
  showVoiceSettings = false,
  onWebSocketAudioData,
  showTextInput = true
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  // Audio visualization refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  // Handle key press for text input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled && !loading && !isRecording) {
      e.preventDefault();
      onSend();
    }
  };

  // Auto-resize textarea based on content
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  // Adjust height when value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle audio context ready from VoiceRecorder
  const handleAudioContextReady = useCallback((audioContext: AudioContext, analyser: AnalyserNode) => {
    console.log('ChatInput: Audio context ready from VoiceRecorder');
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
  }, []);

  // Handle audio data from VoiceRecorder
  const handleAudioData = useCallback((audioBlob: Blob) => {
    console.log(`📤 [CHATINPUT] Received audio data (${audioBlob.size} bytes)`);
    console.log(`📤 [CHATINPUT] Audio details:`, {
      size: audioBlob.size,
      type: audioBlob.type,
      timestamp: new Date().toISOString(),
      hasWebSocketHandler: !!onWebSocketAudioData
    });
    
    // Send audio to WebSocket
    if (onWebSocketAudioData) {
      console.log(`📤 [CHATINPUT] Sending audio to WebSocket`);
      onWebSocketAudioData(audioBlob);
    } else {
      console.error(`❌ [CHATINPUT] No WebSocket handler available`);
    }
  }, [onWebSocketAudioData]);

  // Draw audio visualization using VoiceRecorder's audio context
  const drawVisualization = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      if (!isRecording) return;
      
      requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = 'rgb(241, 244, 248)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 0.75;
      let barHeight;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;
        
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#4e54c8');
        gradient.addColorStop(1, '#8f94fb');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };
    
    draw();
  }, [isRecording]);

  // Update recording time when recording
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    console.log('ChatInput: isRecording changed to:', isRecording);
    
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording]);

  // Start visualization when recording
  useEffect(() => {
    if (isRecording) {
      drawVisualization();
    }
  }, [isRecording, drawVisualization]);

  return (
    <div className="space-y-3 mt-4">
      {/* Voice Controls Row */}
      <div className="flex items-center justify-between hidden">
        <div className="flex items-center space-x-2">
          {/* Voice Toggle */}
          <button
            onClick={onVoiceToggle}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              voiceEnabled 
                ? 'bg-gray-100 text-gray-700 hover:bg-primary-200' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {voiceEnabled ? 'Voice On' : 'Voice Off'}
          </button>
          
        </div>
        

      </div>


      {/* Main Input Area */}
      <div className="flex gap-2">
        {/* Voice Recorder Button */}
        {voiceEnabled && (
          <div className="flex-shrink-0">
            <VoiceRecorder
              onAudioData={handleAudioData}
              onTranscription={onLiveTranscriptionUpdate}
              isRecording={isRecording}
              onRecordingStateChange={setIsRecording}
              disabled={disabled}
              compact={true}
              onAudioContextReady={handleAudioContextReady}
              externalCanvasRef={canvasRef}
            />
          </div>
        )}
        
        {/* Text Input or Audio Visualization */}
        {isRecording ? (
          // Audio Visualization during recording
          <div className="flex flex-1 relative items-center justify-center">
            <canvas
              ref={canvasRef}
              width={400}
              height={60}
              className="w-full h-[60px] rounded-md border-2 border-primary-500 bg-gray-900"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-sm font-medium">
                {formatTime(recordingTime)}
              </div>
            </div>
          </div>
        ) : showTextInput ? (
          // Normal text input when not recording and showTextInput is true
          <div className="flex-1 relative px-1">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              disabled={disabled || loading || isVoiceProcessing}
              placeholder={voiceEnabled ? "Type your response or use voice... (Shift+Enter for new line)" : "Type your response... (Shift+Enter for new line)"}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 resize-none overflow-hidden min-h-[80px] max-h-[200px]"
              rows={1}
            />
            {isVoiceProcessing && (
              <div className="absolute inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center rounded-md">
                <div className="flex items-center space-x-2 text-gray-600">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm">Thinking</span>
                </div>
              </div>
            )}
          </div>
        ) : null}
        
        {/* Send Button - Only show when text input is visible and has content */}
        {showTextInput && value.trim() && (
          <button
            onClick={onSend}
            disabled={disabled || loading || isRecording || isVoiceProcessing}
            className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
            title="Send message"
          >
            {loading || isVoiceProcessing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Error Display */}
      {false && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-sm text-red-800">
            <strong>Voice Error:</strong> Connection failed
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInput; 